import type {
  AnnotationConditionGroup,
  AnnotationConditionOperator,
  AnnotationConditionOccupancySnapshot,
  AnnotationConditionRule,
  AnnotationOccupancyConditionKey,
  AnnotationStateRecord,
  AnnotationStateValue,
  AnnotationTriggerEvent,
} from '../types/worldAnnotation';

const MAX_CONDITION_RULES = 8;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const sanitizeToken = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const toStateValue = (value: unknown): AnnotationStateValue | undefined => {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    isFiniteNumber(value)
  ) {
    return value;
  }
  return undefined;
};

const normalizeOperator = (value: unknown): AnnotationConditionOperator =>
  value === 'neq' || value === 'gt' || value === 'gte' || value === 'lt' || value === 'lte'
    ? value
    : 'eq';

const normalizeOccupancyConditionKey = (
  value: unknown,
): AnnotationOccupancyConditionKey | undefined => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return normalized === 'occupied_count' ||
    normalized === 'capacity' ||
    normalized === 'available_slots' ||
    normalized === 'is_full'
    ? normalized
    : undefined;
};

const toOccupancyConditionValue = (params: {
  key: AnnotationOccupancyConditionKey;
  value: unknown;
}): number | boolean | undefined => {
  if (params.key === 'is_full') {
    return typeof params.value === 'boolean' ? params.value : undefined;
  }

  return isFiniteNumber(params.value) ? params.value : undefined;
};

const normalizeStateRecord = (value: unknown): AnnotationStateRecord | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const entries = Object.entries(value as Record<string, unknown>)
    .map(([key, entry]) => [sanitizeToken(key), toStateValue(entry)] as const)
    .filter(
      (entry): entry is [string, AnnotationStateValue] =>
        Boolean(entry[0]) && entry[1] !== undefined,
    )
    .slice(0, MAX_CONDITION_RULES);

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

const normalizeConditionRule = (value: unknown): AnnotationConditionRule | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const candidate = value as Record<string, unknown>;
  const source =
    candidate.source === 'event_detail' ||
    candidate.source === 'utc_minutes' ||
    candidate.source === 'occupancy'
      ? candidate.source
      : 'state';
  const operator = normalizeOperator(candidate.operator);

  if (source === 'utc_minutes') {
    const numericValue = Number(candidate.value);
    if (!Number.isFinite(numericValue)) {
      return undefined;
    }
    return {
      source,
      operator,
      value: Math.max(0, Math.min(1_439, Math.floor(numericValue))),
    };
  }

  if (source === 'occupancy') {
    const key = normalizeOccupancyConditionKey(candidate.key) ?? null;
    if (!key) {
      return undefined;
    }

    const normalizedValue = toOccupancyConditionValue({ key, value: candidate.value });
    if (normalizedValue === undefined) {
      return undefined;
    }

    return {
      source,
      key,
      operator,
      value: normalizedValue,
    };
  }

  const key = sanitizeToken(candidate.key);
  const normalizedValue = toStateValue(candidate.value);
  if (!key || normalizedValue === undefined) {
    return undefined;
  }

  return {
    source,
    key,
    operator,
    value: normalizedValue,
  };
};

export const cloneAnnotationConditionRule = (
  rule: AnnotationConditionRule,
): AnnotationConditionRule => ({ ...rule });

export const cloneAnnotationConditionGroup = (
  value: AnnotationConditionGroup | undefined,
): AnnotationConditionGroup | undefined =>
  value
    ? {
        mode: value.mode === 'any' ? 'any' : 'all',
        rules: value.rules.map(cloneAnnotationConditionRule),
      }
    : undefined;

export const buildLegacyConditionGroup = (value: unknown): AnnotationConditionGroup | undefined => {
  const record = normalizeStateRecord(value);
  if (!record) {
    return undefined;
  }

  return {
    mode: 'all',
    rules: Object.entries(record).map(([key, entry]) => ({
      source: 'state' as const,
      key,
      operator: 'eq' as const,
      value: entry,
    })),
  };
};

export const normalizeAnnotationConditionGroup = (
  value: unknown,
): AnnotationConditionGroup | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const candidate = value as Record<string, unknown>;
  if (!Array.isArray(candidate.rules)) {
    return undefined;
  }

  const rules = candidate.rules
    .slice(0, MAX_CONDITION_RULES)
    .map(normalizeConditionRule)
    .filter((rule): rule is AnnotationConditionRule => Boolean(rule));

  if (rules.length === 0) {
    return undefined;
  }

  return {
    mode: candidate.mode === 'any' ? 'any' : 'all',
    rules,
  };
};

const compareConditionValues = (params: {
  left: AnnotationStateValue | undefined;
  operator: AnnotationConditionOperator;
  right: AnnotationStateValue | number;
}): boolean => {
  if (params.operator === 'eq') {
    return params.left === params.right;
  }
  if (params.operator === 'neq') {
    return params.left !== params.right;
  }

  if (typeof params.left !== 'number' || typeof params.right !== 'number') {
    return false;
  }

  if (params.operator === 'gt') {
    return params.left > params.right;
  }
  if (params.operator === 'gte') {
    return params.left >= params.right;
  }
  if (params.operator === 'lt') {
    return params.left < params.right;
  }
  return params.left <= params.right;
};

const compareOccupancyConditionValues = (params: {
  left: AnnotationConditionOccupancySnapshot[AnnotationOccupancyConditionKey] | undefined;
  operator: AnnotationConditionOperator;
  right: number | boolean;
}): boolean => {
  if (typeof params.right === 'boolean') {
    if (typeof params.left !== 'boolean') {
      return false;
    }

    if (params.operator === 'eq') {
      return params.left === params.right;
    }
    if (params.operator === 'neq') {
      return params.left !== params.right;
    }
    return false;
  }

  if (typeof params.left !== 'number') {
    return false;
  }

  return compareConditionValues({
    left: params.left,
    operator: params.operator,
    right: params.right,
  });
};

export const getUtcMinutesFromEpochMs = (nowMs: number): number => {
  const date = new Date(nowMs);
  return date.getUTCHours() * 60 + date.getUTCMinutes();
};

export const annotationConditionGroupMatches = (params: {
  conditions: AnnotationConditionGroup | undefined;
  currentState: AnnotationStateRecord | undefined;
  eventDetail: AnnotationStateRecord | undefined;
  occupancy?: AnnotationConditionOccupancySnapshot | undefined;
  nowMs: number;
  triggerEvent: AnnotationTriggerEvent;
}): boolean => {
  if (!params.conditions || params.conditions.rules.length === 0) {
    return true;
  }

  const evaluations = params.conditions.rules.map((rule) => {
    if (rule.source === 'utc_minutes') {
      return compareConditionValues({
        left: getUtcMinutesFromEpochMs(params.nowMs),
        operator: rule.operator,
        right: rule.value,
      });
    }

    if (rule.source === 'occupancy') {
      return compareOccupancyConditionValues({
        left: params.occupancy?.[rule.key],
        operator: rule.operator,
        right: rule.value,
      });
    }

    const sourceRecord =
      rule.source === 'event_detail'
        ? params.eventDetail
        : params.triggerEvent === 'state_change'
          ? params.eventDetail
          : params.currentState;

    return compareConditionValues({
      left: sourceRecord?.[rule.key],
      operator: rule.operator,
      right: rule.value,
    });
  });

  if (params.conditions.mode === 'any') {
    return evaluations.some(Boolean);
  }
  return evaluations.every(Boolean);
};

export const utcMinutesToTimeInputValue = (value: number): string => {
  const normalized = Math.max(0, Math.min(1_439, Math.floor(value)));
  const hours = Math.floor(normalized / 60)
    .toString()
    .padStart(2, '0');
  const minutes = (normalized % 60).toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const timeInputValueToUtcMinutes = (value: string): number | null => {
  const trimmed = value.trim();
  if (!/^\d{2}:\d{2}$/.test(trimmed)) {
    return null;
  }

  const [hoursToken, minutesToken] = trimmed.split(':');
  const hours = Number(hoursToken);
  const minutes = Number(minutesToken);
  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return hours * 60 + minutes;
};
